import type { ReactElement, ReactNode } from 'react'
import {
  Bar,
  BarShapeProps,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  Rectangle,
  ResponsiveContainer,
  Tooltip,
  TooltipContentProps,
  XAxis,
  YAxis
} from 'recharts'

export type BarLineChartDataPoint = Record<string, unknown>

export type BarLineChartBarSeries = {
  dataKey: string
  name: string
  fill: string
  fillOpacity?: number
  barSize?: number
  getCellFill?: (entry: BarLineChartDataPoint, index: number) => string
}

export type BarLineChartLineDotProps = {
  cx?: number
  cy?: number
  payload?: BarLineChartDataPoint
  index?: number
}

export type BarLineChartLineSeries = {
  dataKey: string
  name: string
  stroke: string
  strokeWidth?: number
  strokeDasharray?: string
  dot?: boolean | ((props: BarLineChartLineDotProps) => ReactNode)
}

export type BarLineChartTooltipContent = (props: TooltipContentProps<number, string>) => ReactNode

export type BarLineChartProps = {
  data: BarLineChartDataPoint[]
  xKey: string
  bars?: BarLineChartBarSeries[]
  lines?: BarLineChartLineSeries[]
  tooltip?: BarLineChartTooltipContent
  legend?: ReactElement
  className?: string
  testId?: string
  gridColor?: string
  xTickClassName?: string
}

const renderBarShape = (bar: BarLineChartBarSeries) => (props: BarShapeProps) => {
  const { x, y, width, height, fillOpacity, index, payload } = props
  const fill = bar.getCellFill?.(payload as BarLineChartDataPoint, index) ?? bar.fill

  return (
    <Rectangle x={x} y={y} width={width} height={height} fill={fill} fillOpacity={fillOpacity ?? bar.fillOpacity} />
  )
}

const BarLineChart = ({
  data,
  xKey,
  bars = [],
  lines = [],
  tooltip: TooltipContent,
  legend,
  className = 'mg-bar-line-chart',
  testId,
  gridColor = 'var(--vp-semantic-color-border-quiet)',
  xTickClassName
}: BarLineChartProps): ReactElement => (
  <div className={className} data-testid={testId}>
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
        <CartesianGrid vertical={false} stroke={gridColor} strokeDasharray="1 3" />
        <YAxis hide domain={[0, 'dataMax']} />
        <XAxis
          dataKey={xKey}
          tickLine={false}
          axisLine={false}
          interval={0}
          tick={xTickClassName ? { className: xTickClassName } : undefined}
        />
        {TooltipContent && <Tooltip content={TooltipContent} />}
        {bars.map(bar => (
          <Bar
            key={bar.dataKey}
            dataKey={bar.dataKey}
            name={bar.name}
            fill={bar.fill}
            fillOpacity={bar.fillOpacity}
            barSize={bar.barSize}
            shape={bar.getCellFill ? renderBarShape(bar) : undefined}
          />
        ))}
        {lines.map(line => (
          <Line
            key={line.dataKey}
            type="linear"
            dataKey={line.dataKey}
            name={line.name}
            stroke={line.stroke}
            strokeWidth={line.strokeWidth ?? 2}
            strokeDasharray={line.strokeDasharray}
            dot={line.dot ?? false}
            activeDot={false}
          />
        ))}
        {legend && <Legend verticalAlign="bottom" align="center" content={legend} />}
      </ComposedChart>
    </ResponsiveContainer>
  </div>
)

export default BarLineChart
